import { useState, useEffect, useCallback, useRef } from "react";
import {
  compareClientVersions,
  isClientRelease,
  parseClientVersion,
  type ClientRelease,
} from "../utils/client-release";

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000;
const INITIAL_CHECK_DELAY = 10 * 1000;
const SNOOZE_DURATION = 60 * 60 * 1000;
const DEPLOY_GRACE_PERIOD = 10 * 60 * 1000;
const CONSECUTIVE_READS_TO_PROMPT = 2;

async function fetchLatestRelease(
  signal: AbortSignal
): Promise<ClientRelease | null> {
  try {
    const response = await fetch(`/version/release?t=${Date.now()}`, {
      cache: "no-store",
      signal,
    });
    if (
      response.status !== 200 ||
      !response.headers
        .get("content-type")
        ?.toLowerCase()
        .startsWith("application/json")
    )
      return null;
    const body: unknown = await response.json();
    return isClientRelease(body) ? body : null;
  } catch {
    // Offline, WAF responses, and unavailable metadata are not releases.
    return null;
  }
}

/** Only selected releases interrupt users; the baseline belongs to this bundle. */
export function useVersionCheck() {
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);
  const currentVersion = useRef(process.env.SKED_CLIENT_VERSION || "");
  const snoozedUntil = useRef(0);
  const pendingThreshold = useRef<string | null>(null);
  const pendingCount = useRef(0);
  const firstMismatchAt = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);

  const checkVersion = useCallback(async () => {
    if (
      !parseClientVersion(currentVersion.current) ||
      Date.now() < snoozedUntil.current ||
      activeRequest.current
    )
      return;
    const controller = new AbortController();
    activeRequest.current = controller;
    const timeout = setTimeout(() => controller.abort(), 10_000);
    try {
      const release = await fetchLatestRelease(controller.signal);
      if (controller.signal.aborted) {
        if (activeRequest.current === controller) {
          pendingThreshold.current = null;
          pendingCount.current = 0;
        }
        return;
      }
      if (!release) {
        pendingThreshold.current = null;
        pendingCount.current = 0;
        return;
      }
      const threshold = release.refreshBeforeVersion;
      if (
        !threshold ||
        compareClientVersions(currentVersion.current, threshold) >= 0
      ) {
        pendingThreshold.current = null;
        pendingCount.current = 0;
        setIsNewVersionAvailable(false);
        return;
      }
      // A routine deploy preserves the threshold and doesn't restart the grace period.
      if (pendingThreshold.current === threshold) {
        pendingCount.current++;
      } else {
        pendingThreshold.current = threshold;
        pendingCount.current = 1;
        firstMismatchAt.current = Date.now();
      }
      if (
        pendingCount.current >= CONSECUTIVE_READS_TO_PROMPT &&
        Date.now() - firstMismatchAt.current >= DEPLOY_GRACE_PERIOD
      )
        setIsNewVersionAvailable(true);
    } finally {
      clearTimeout(timeout);
      if (activeRequest.current === controller) activeRequest.current = null;
    }
  }, []);

  const dismissUpdate = useCallback(() => {
    snoozedUntil.current = Date.now() + SNOOZE_DURATION;
    setIsNewVersionAvailable(false);
  }, []);
  const triggerReload = useCallback(() => window.location.reload(), []);

  useEffect(() => {
    const initialTimeout = setTimeout(checkVersion, INITIAL_CHECK_DELAY);
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void checkVersion();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      activeRequest.current?.abort();
      activeRequest.current = null;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkVersion]);

  return { isNewVersionAvailable, dismissUpdate, triggerReload };
}
